import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  FiPlus,
  FiAlertCircle,
  FiCheckCircle,
  FiTrash2,
  FiEdit,
  FiFilter,
} from "react-icons/fi";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { makeRequestAuth } from "../../_config/api";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import Spinner from "../../utils/Loader";
import UpdateBannerModal from "./UpdateBannerModal";

function AllBanner() {
  const [banners, setBanners] = useState([]);
  const [filterType, setFilterType] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Modal States
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);

  const [actionModal, setActionModal] = useState({
    open: false,
    type: "", // 'status' or 'delete'
    id: null,
    isDisable: null,
    message: "",
  });

  const fetchBanners = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = filterType ? { type: filterType } : {};
      const response = await makeRequestAuth(
        "get",
        "/admin/getAllBanner",
        params,
      );
      const bannerData = response?.data || [];
      setBanners(bannerData);
    } catch (err) {
      showErrorToast(err.message || "Failed to fetch banners");
    } finally {
      setIsLoading(false);
    }
  }, [filterType]);

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  // Handle Edit Action
  const openEditModal = (item) => {
    setSelectedBanner(item);
    setIsEditModalOpen(true);
  };

  // Handle Status Toggle Action
  const handleStatusToggle = async () => {
    const { id } = actionModal;
    try {
      await makeRequestAuth("post", "/admin/updateBannerStatus", { id });
      setBanners((prev) =>
        prev.map((item) =>
          item._id === id ? { ...item, isDisable: !item.isDisable } : item,
        ),
      );
      showSuccessToast("Status updated successfully");
    } catch (err) {
      showErrorToast("Failed to update status");
    } finally {
      closeActionModal();
    }
  };

  // Handle Delete Action
  const handleDelete = async () => {
    const { id } = actionModal;
    try {
      await makeRequestAuth("post", "/admin/deleteBanner", { id });
      setBanners((prev) => prev.filter((item) => item._id !== id));
      showSuccessToast("Banner deleted successfully");
    } catch (err) {
      showErrorToast("Failed to delete banner");
    } finally {
      closeActionModal();
    }
  };

  const openActionModal = (type, item) => {
    setActionModal({
      open: true,
      type: type, // 'status' or 'delete'
      id: item._id,
      isDisable: item.isDisable,
      message:
        type === "delete"
          ? "Are you sure you want to permanently delete this banner?"
          : `Are you sure you want to ${item.isDisable ? "Activate" : "Deactivate"} this banner?`,
    });
  };

  const closeActionModal = () => {
    setActionModal({
      open: false,
      type: "",
      id: null,
      isDisable: null,
      message: "",
    });
  };

  if (isLoading) return <Spinner />;

  return (
    <div className="grid grid-cols-1 gap-6 p-4">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="bg-white dark:bg-[#1E2738] text-gray-800 dark:text-white rounded-2xl p-6 md:p-8 border border-purple-300 dark:border-purple-600/50 shadow-xl">
        {/* HEADER */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h2 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Banner Management
            </h2>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            {/* Filter Dropdown */}
            <div className="relative w-full sm:w-48">
              <FiFilter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-800 dark:text-white focus:ring-2 focus:ring-purple-500 outline-none cursor-pointer"
              >
                <option value="">All Types</option>
                <option value="top">Top Banners</option>
                <option value="bottom">Bottom Banners</option>
              </select>
            </div>

            <Link to="/banner/create">
              <button className="flex items-center justify-center gap-2 w-full sm:w-auto bg-gradient-to-r from-blue-600 to-purple-600 text-white px-5 py-2 rounded-xl font-medium shadow-lg hover:scale-[1.02] transition">
                <FiPlus size={18} />
                <span>Add Banner</span>
              </button>
            </Link>
          </div>
        </div>

        {/* TABLE */}
        <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full table-auto text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800/50 text-gray-600 dark:text-gray-300 text-sm uppercase tracking-wider font-semibold border-b border-gray-200 dark:border-gray-700">
                  <th className="py-4 px-6 w-16 text-center">#</th>
                  <th className="py-4 px-6">Image</th>
                  <th className="py-4 px-6 text-center">Type</th>
                  <th className="py-4 px-6 text-center">Status</th>
                  <th className="py-4 px-6 text-right">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {banners.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center py-10 text-gray-500">
                      No Banners Found
                    </td>
                  </tr>
                ) : (
                  banners.map((item, index) => (
                    <tr
                      key={item._id}
                      className={`hover:bg-blue-50/50 dark:hover:bg-gray-700/30 transition ${item.isDisable ? "opacity-60" : ""}`}
                    >
                      <td className="py-4 px-6 text-center">{index + 1}</td>

                      <td className="py-4 px-6">
                        <img
                          src={item.image}
                          alt={item.type}
                          className="h-12 w-24 object-cover rounded shadow-sm border border-gray-200 dark:border-gray-600"
                        />
                      </td>

                      <td className="py-4 px-6 text-center">
                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                          {item.type}
                        </span>
                      </td>

                      <td className="py-4 px-6 text-center">
                        <span
                          className={`text-xs font-bold px-2 py-1 rounded ${item.isDisable ? "bg-red-100 text-red-600" : "bg-emerald-100 text-emerald-600"}`}
                        >
                          {item.isDisable ? "INACTIVE" : "ACTIVE"}
                        </span>
                      </td>

                      <td className="py-4 px-6 text-right">
                        <div className="flex justify-end items-center gap-3">
                          {/* EDIT BUTTON */}
                          <button
                            onClick={() => openEditModal(item)}
                            className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition dark:text-blue-400 dark:hover:bg-blue-900/30"
                            title="Edit"
                          >
                            <FiEdit size={18} />
                          </button>

                          {/* DELETE BUTTON */}
                          <button
                            onClick={() => openActionModal("delete", item)}
                            className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition dark:text-red-400 dark:hover:bg-red-900/30"
                            title="Delete"
                          >
                            <FiTrash2 size={18} />
                          </button>

                          {/* TOGGLE STATUS */}
                          <button
                            onClick={() => openActionModal("status", item)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${!item.isDisable ? "bg-emerald-500" : "bg-gray-300 dark:bg-gray-600"}`}
                          >
                            <span
                              className={`${!item.isDisable ? "translate-x-6" : "translate-x-1"} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                            />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* --- REUSABLE ACTION MODAL (Status & Delete) --- */}
      {actionModal.open && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white dark:bg-[#1E2738] p-8 rounded-2xl shadow-2xl max-w-sm w-full text-center border border-gray-200 dark:border-gray-600 animate-fadeIn">
            <div
              className={`mx-auto flex items-center justify-center h-16 w-16 rounded-full mb-6 ${
                actionModal.type === "delete"
                  ? "bg-red-100 text-red-600"
                  : actionModal.isDisable
                    ? "bg-emerald-100 text-emerald-600"
                    : "bg-yellow-100 text-yellow-600"
              }`}
            >
              {actionModal.type === "delete" ? (
                <FiTrash2 size={32} />
              ) : actionModal.isDisable ? (
                <FiCheckCircle size={32} />
              ) : (
                <FiAlertCircle size={32} />
              )}
            </div>

            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              {actionModal.type === "delete"
                ? "Delete Banner?"
                : "Update Status?"}
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-8">
              {actionModal.message}
            </p>

            <div className="flex justify-center gap-3">
              <button
                onClick={closeActionModal}
                className="flex-1 px-5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={
                  actionModal.type === "delete"
                    ? handleDelete
                    : handleStatusToggle
                }
                className={`flex-1 px-5 py-2.5 rounded-xl text-white font-medium shadow-lg transition ${
                  actionModal.type === "delete"
                    ? "bg-red-600 hover:bg-red-700"
                    : actionModal.isDisable
                      ? "bg-emerald-600 hover:bg-emerald-700"
                      : "bg-yellow-500 hover:bg-yellow-600"
                }`}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      <UpdateBannerModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        fetchBanners={fetchBanners}
        initialData={selectedBanner}
      />
    </div>
  );
}

export default AllBanner;
